import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ArrowUp } from 'lucide-react';
import { ThemeColors, Spacing, Shadows, BorderRadius } from '../../../styles/ThemeStyles';

const BackToTopButtonContainer = styled.button<{ isVisible: boolean }>`
  position: fixed;
  bottom: ${Spacing.lg};
  right: ${Spacing.lg};
  width: 50px;
  height: 50px;
  background: ${ThemeColors.primary.main};
  color: white;
  border: none;
  border-radius: ${BorderRadius.full};
  box-shadow: ${Shadows.lg};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${ThemeColors.primary.dark};
    transform: translateY(-3px);
    box-shadow: ${Shadows.xl};
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    bottom: ${Spacing.md};
    right: ${Spacing.md};
  }
  
  @media (max-width: 480px) {
    width: 44px;
    height: 44px;
    bottom: ${Spacing.sm};
    right: ${Spacing.sm};
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface BackToTopButtonProps {
  scrollThreshold?: number; // Distance in pixels to scroll before showing button
}

export const BackToTopButton: React.FC<BackToTopButtonProps> = ({ 
  scrollThreshold = 300 
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsVisible(scrollTop > scrollThreshold);
    };

    // Check initial scroll position
    handleScroll();

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollThreshold]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <BackToTopButtonContainer
      isVisible={isVisible}
      onClick={scrollToTop}
      aria-label="Back to top"
      title="Back to top"
    >
      <IconWrapper>
        <ArrowUp size={24} />
      </IconWrapper>
    </BackToTopButtonContainer>
  );
};

